import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import PaymentsIcon from '@mui/icons-material/Payments';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import BoltIcon from '@mui/icons-material/Bolt';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import HealingIcon from '@mui/icons-material/Healing';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';
import { compactNumber, contextNameKey, countdown, isMaxLevel, liveEnergy } from '../../utils/theFreaks';
import useFreaksT from './useFreaksT';
import { contextIcon } from './contextIcon';
import { barSx, FREAKS_DARK, FREAKS_GOLD, FREAKS_RED } from './freaksStyles';

const Stat = ({ icon, label, value, color }) => (
  <Box sx={{ flex: 1, textAlign: 'center', minWidth: 0 }} aria-label={`${label}: ${value}`}>
    {React.cloneElement(icon, { sx: { fontSize: 18, color } })}
    <Typography noWrap sx={{ fontSize: 20, fontWeight: 800, color, lineHeight: 1.2 }}>{value}</Typography>
    <Typography noWrap sx={{ color: '#999', fontSize: 12 }}>{label}</Typography>
  </Box>
);

// Top of the game: who I am, my activity, cash / respect / power, the energy bar and an injury banner.
export default function FreaksHero({ state, now, busy, onPickContext, onRefill, onRecover }) {
  const { t } = useFreaksT();
  const { energy, next } = liveEnergy(state.energy, state.maxEnergy, state.nextEnergyAt, now);
  const recovering = state.recovery && new Date(state.recovery.until).getTime() > now;

  return (
    <Box sx={{ backgroundColor: FREAKS_DARK, borderRadius: 4.5, p: 2, mb: 1.5, color: 'white' }} data-testid="freaks-hero">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 900, letterSpacing: 2 }}>{t('theFreaks.title').toUpperCase()}</Typography>
          <Typography noWrap sx={{ color: '#bbb', mt: 0.25 }}>
            @{state.username} · {t(isMaxLevel(state.level, state.maxLevel) ? 'theFreaks.levelMax' : 'theFreaks.level', { level: state.level })}
          </Typography>
        </Box>
        <ButtonBase
          data-testid="freaks-context"
          onClick={onPickContext}
          aria-label={`${t('theFreaks.playAs')}: ${t(contextNameKey(state.context))}`}
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.75, border: '1px solid #333', borderRadius: 5,
            px: 1.25, minHeight: 44, maxWidth: '50%', color: 'white',
          }}
        >
          {contextIcon(state.context, { color: DEFAULT_THEME_COLOR })}
          <Typography noWrap sx={{ fontSize: 14 }}>{t(contextNameKey(state.context))}</Typography>
          <ExpandMoreIcon sx={{ fontSize: 18, color: '#ccc' }}/>
        </ButtonBase>
      </Box>

      <Box sx={{ display: 'flex', mt: 2 }}>
        <Stat icon={<PaymentsIcon/>} label={t('theFreaks.cash')} value={compactNumber(state.cash)} color={FREAKS_GOLD}/>
        <Stat icon={<WorkspacePremiumIcon/>} label={t('theFreaks.respect')} value={compactNumber(state.respect)} color={DEFAULT_THEME_COLOR}/>
        <Stat icon={<FitnessCenterIcon/>} label={t('theFreaks.power')} value={compactNumber(state.power)} color="#fff"/>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
        <BoltIcon sx={{ fontSize: 18, color: FREAKS_GOLD }}/>
        <Typography sx={{ fontWeight: 700, ml: 0.75, flex: 1 }} data-testid="freaks-energy">
          {t('theFreaks.energy')} {energy}/{state.maxEnergy}
        </Typography>
        <Typography sx={{ color: '#999', fontSize: 12 }}>
          {next ? t('theFreaks.nextEnergy', { time: countdown(new Date(next), now) }) : t('theFreaks.energyFull')}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={Math.min(100, (energy / Math.max(1, state.maxEnergy)) * 100)}
        sx={{ ...barSx(FREAKS_GOLD), height: 8, borderRadius: 4, mt: 0.75 }}
      />
      <Button
        data-testid="freaks-refill"
        startIcon={<LocalFireDepartmentIcon/>}
        disabled={busy || state.refill.left <= 0 || energy >= state.maxEnergy}
        onClick={onRefill}
        sx={{
          mt: 0.5, ml: -1, minHeight: 44, textTransform: 'none', color: FREAKS_GOLD, textAlign: 'left',
          '&.Mui-disabled': { color: 'rgba(245,197,66,0.4)' },
        }}
      >
        {t('theFreaks.refill', { energy: state.refill.energy })} · {t('theFreaks.refillPrice', { price: state.refill.price, left: state.refill.left })}
      </Button>

      {recovering ? (
        <Box
          data-testid="freaks-recovery"
          sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, backgroundColor: FREAKS_RED, borderRadius: 3, p: 1.25, mt: 1 }}
        >
          <HealingIcon sx={{ color: 'white' }}/>
          <Box sx={{ flex: 1, minWidth: 140 }}>
            <Typography sx={{ fontWeight: 700 }}>{t('theFreaks.recoveryTitle')}</Typography>
            <Typography sx={{ fontSize: 12 }}>{t('theFreaks.recoveryText', { time: countdown(state.recovery.until, now) })}</Typography>
          </Box>
          <Button
            variant="contained"
            disabled={busy}
            onClick={onRecover}
            sx={{ minHeight: 40, backgroundColor: 'white', color: FREAKS_RED, textTransform: 'none', fontWeight: 700, '&:hover': { backgroundColor: '#eee' } }}
          >
            {t('theFreaks.recoverButton', { price: state.recovery.price })}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}
